import { EmployeeTimeoffPoliciesFromLeavesQuery } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { convertDate, dateToString } from 'modules/core/lib/dates'
import { stringToDate } from 'modules/core/lib/dates'

import { leaveValidFinishOn } from 'modules/timeoff/lib/leaveEmployees'
import rangeByDay from 'modules/timeoff/lib/rangeByDay'
import companyHolidaysStore from 'modules/timeoff/store/collections/companyHolidays'
import type BlockedPeriod from 'modules/timeoff/store/models/BlockedPeriods/BlockedPeriod'
import type CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'
import type Leave from 'modules/timeoff/store/models/Leave'

export type CompanyHolidaysTree = Record<string, CompanyHoliday[]>
export type BlockedPeriodsTree = Record<string, BlockedPeriod[]>
export type LeavesTree = Record<string, LeavesTree[]>

export interface DayLeaf {
  blockedPeriods: BlockedPeriod[]
  companyHolidays: CompanyHoliday[]
  leaves: Leave[]
}

/*
  MunchedTree is an object where each key is one day of the year and the value is a DayLeaf:
  {
    '2020-01-01': {
      blockedPeriods: [blockedPeriod, blockedPeriod, blockedPeriod],
      companyHolidays: [companyHoliday, companyHoliday, companyHoliday],
      leaves: [leave, leave, leave],
    },
    '2020-01-02': {
      blockedPeriods: [blockedPeriod],
      companyHolidays: [],
      leaves: [leave, leave, leave],
    },
  }
*/
export interface MunchedTree {
  [day: string]: DayLeaf
}

interface Props {
  leaves: Leave[]
  blockedPeriods: BlockedPeriod[]
  year: number
  employeeTimeoffPoliciesFromLeavesData: EmployeeTimeoffPoliciesFromLeavesQuery | null
}

class CalendarStore {
  year: number
  leaves: Leave[]
  blockedPeriods: BlockedPeriod[]
  tree: MunchedTree = {}
  employeeTimeoffPoliciesFromLeavesData: EmployeeTimeoffPoliciesFromLeavesQuery | null

  constructor(props: Props) {
    this.year = props.year
    this.leaves = props.leaves
    this.blockedPeriods = props.blockedPeriods
    this.employeeTimeoffPoliciesFromLeavesData = props.employeeTimeoffPoliciesFromLeavesData
    makeObservable(this, {
      companyHolidaysTree: computed,
      blockedPeriodsTree: computed,
      leavesTree: computed,
      getTree: computed,
    })
  }

  get getTree(): MunchedTree {
    const days = rangeByDay(moment([this.year]), moment([this.year]).endOf('year'))
    this.tree = days.reduce<MunchedTree>((memo, day) => {
      const key = dateToString(day, 'backend')
      const result = {
        blockedPeriods: this.blockedPeriodsTree[key] || [],
        companyHolidays: this.companyHolidaysTree[key] || [],
        leaves: this.leavesTree[key] || [],
      }

      if (result.blockedPeriods.length || result.companyHolidays.length || result.leaves.length) {
        memo[key] = result
      }
      return memo
    }, {})

    return this.tree
  }

  /*
  CompanyHolidaysTree is an object with the following structure:
  {
    '2020-01-01': [CompanyHoliday],
    '2020-01-02': [CompanyHoliday]
    ...
  }
  */
  get companyHolidaysTree(): Record<string, CompanyHoliday[]> {
    if (!companyHolidaysStore.models.length) return {}

    return companyHolidaysStore.models.reduce<Record<string, CompanyHoliday[]>>((memo, holiday) => {
      const formattedDate = convertDate(holiday.attributes.date, 'backend', 'backend') // '2022-01-01'
      if (!memo[formattedDate]) memo[formattedDate] = [holiday]
      return memo
    }, {})
  }

  /*
  BlockedPeriodsTree is an object with the following structure:
  {
    '2020-01-01': [blockedPeriod, blockedPeriod, blockedPeriod],
    '2020-01-02': [blockedPeriod, blockedPeriod, blockedPeriod],
    '2020-01-03': [blockedPeriod],
    ...
  }
  */
  get blockedPeriodsTree(): BlockedPeriodsTree {
    if (!this.blockedPeriods.length) return {}

    const blockedPeriodsSortedByRange = this.blockedPeriods.sort(
      (nextBlockedPeriod, blockedPeriod) => {
        const nextStart = stringToDate(nextBlockedPeriod.attributes.start_on, 'backend')
        const start = stringToDate(blockedPeriod.attributes.start_on, 'backend')
        const nextRange = rangeByDay(
          nextStart,
          stringToDate(nextBlockedPeriod.attributes.finish_on, 'backend')
        )
        const range = rangeByDay(start, stringToDate(blockedPeriod.attributes.finish_on, 'backend'))

        return range.length - nextRange.length
      }
    )

    return blockedPeriodsSortedByRange.reduce<Record<string, BlockedPeriod[]>>(
      (memo: Record<string, BlockedPeriod[]>, blockedPeriod: BlockedPeriod) => {
        const start = moment.max([
          stringToDate(blockedPeriod.attributes.start_on, 'backend'),
          moment([this.year]),
        ])
        const end = moment.min([
          stringToDate(blockedPeriod.attributes.finish_on, 'backend'),
          moment([this.year]).endOf('year'),
        ])

        const range = rangeByDay(start, end)

        range.forEach((date: moment.Moment) => {
          const key = date.format('YYYY-MM-DD')
          if (!memo[key]) memo[key] = []

          memo[key]?.push(blockedPeriod)
        })

        return memo
      },
      {}
    )
  }

  get leavesTree() {
    if (!this.leaves.length) return {}

    const leavesSortedByRange = this.leaves.sort((nextLeave, leave) => {
      const nextStart = stringToDate(nextLeave.attributes.start_on, 'backend')
      const start = stringToDate(leave.attributes.start_on, 'backend')
      const employeeTimeoffPolicyId =
        this.employeeTimeoffPoliciesFromLeavesData?.timeoff.leaves.find(
          (leaveData) => leaveData.id === nextLeave.attributes.id
        )?.employee?.timeoffPolicyId
      const nextRange = rangeByDay(
        nextStart,
        leaveValidFinishOn(nextLeave, employeeTimeoffPolicyId)
      )
      const range = rangeByDay(start, leaveValidFinishOn(leave, employeeTimeoffPolicyId))

      return range.length - nextRange.length
    })

    return leavesSortedByRange.reduce<Record<string, Leave[]>>((memo, leave) => {
      if (leave.attributes.approved === false) return memo

      const start = stringToDate(leave.attributes.start_on, 'backend')
      const employeeTimeoffPolicyId =
        this.employeeTimeoffPoliciesFromLeavesData?.timeoff.leaves.find(
          (leaveData) => leaveData.id === leave.attributes.id
        )?.employee?.timeoffPolicyId
      const range = rangeByDay(start, leaveValidFinishOn(leave, employeeTimeoffPolicyId))

      range.forEach((date: moment.Moment) => {
        const key = date.format('YYYY-MM-DD')
        if (!memo[key]) memo[key] = []

        leave.isSick ? memo[key]?.unshift(leave) : memo[key]?.push(leave)
      })

      return memo
    }, {})
  }
}

export default CalendarStore
